import React, { FC, ChangeEvent } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import triggerTracking from '@blueprint/tracking';

import {
    updateCurrentSortOrder,
    selectSortOrderOptions,
    selectCurrentSortOrder
} from '../../../reducers/listingsSortOrder';
import { filtersValuesSelector } from '../../../reducers/filters';
import { SortOrderDropdownStyle } from './SearchResultsHeader.styles';
import {
    Action as SearchContextAction,
    SearchPageType
} from '../../SearchContext';
import { updateResults } from '../../helpers';

const sortOrderAriaLabel = 'Sort';

type SortOrderDropdownProps = {
    excludeRadiusFromResults: boolean;
    userAlertId: string | null;
    searchType: SearchPageType;
    dispatch: React.Dispatch<SearchContextAction>;
};

export const SortOrderDropdown: FC<SortOrderDropdownProps> = ({
    excludeRadiusFromResults,
    userAlertId,
    searchType,
    dispatch
}) => {
    const reduxDispatch = useDispatch();

    const filters = useSelector(filtersValuesSelector);
    const options = useSelector(selectSortOrderOptions);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const router = useRouter();

    const onChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        const {
            target: { value: sortOrder }
        } = evt;

        reduxDispatch(updateCurrentSortOrder(sortOrder));
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'search results',
            eventAction: 'click',
            eventLabel:
                sortOrder === 'newest_listings'
                    ? 'sort_most_recent'
                    : `sort_${sortOrder}`
        });
        if (userAlertId) {
            dispatch({
                type: 'SET_PREVIOUS_SAVED_SEARCH_URI',
                payload: router.asPath
            });
        }

        updateResults(
            filters,
            router,
            searchType,
            'refine',
            sortOrder,
            userAlertId,
            excludeRadiusFromResults,
            true
        );
    };

    return (
        <SortOrderDropdownStyle
            id="sort-order-dropdown"
            aria-label={sortOrderAriaLabel}
            value={currentSortOrder}
            onChange={onChange}
            data-testid="sort-order-dropdown"
        >
            {options?.map((option) => {
                const { value, label } = option;

                return (
                    <option value={value} key={value}>
                        {label}
                    </option>
                );
            })}
        </SortOrderDropdownStyle>
    );
};
